/**
 * HR → «Холд» (16.10.2026, владелец): «в HR нужен новый субтаб "холд", где будет отображаться текущий баланс (холд)
 * операторов, а так же возможность выдавать им суммы, уменьшая тем самым холд»; «нужно видеть историю выдач
 * с возможностью отката». Холд = заработано за закрытые недели (пт–чт) − выдано: «+» зелёным — мы должны, «−» красным —
 * должен нам. Сверху итоги, ниже строка на каждого: тап по имени — история (недели, выдачи с «Отменить»), «Выдать» —
 * PayoutSheet. После выдачи — тост «Выдано … · Отменить» (10 с). «История выдач» — все выдачи, новые сверху.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { cancelPayout, fetchHolds, holdNote, holdTone, rub, signedRub, weekLabel, type HoldRow, type Holds } from "../../hooks/payoutsApi";
import { errText } from "./BonusPenaltySheet";
import { PayoutSheet, type PayDone } from "./PayoutSheet";
import { AllPayouts, StaffHistory } from "./PayoutHistory";
import board from "./board.module.css";
import css from "./hold.module.css";

type Toast = { t: string; err?: boolean; undo?: { id: number; amount: number } };

export function HoldTab() {
  const [mode, setMode] = useState<"balance" | "history">("balance");
  const [data, setData] = useState<Holds | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const [open, setOpen] = useState<string | null>(null);
  const [sheet, setSheet] = useState<HoldRow | null>(null);
  const [toast, setToast] = useState<Toast | null>(null);
  const timer = useRef<number | undefined>(undefined);
  const pending = useRef(new Map<string, string>());   // staff_id → ключ выдачи с неизвестным исходом (ревью 16.10)
  const undoing = useRef(false);

  const reload = useCallback(() => setTick((n) => n + 1), []);
  useEffect(() => {
    let alive = true;
    fetchHolds().then((d) => { if (alive) { setData(d); setErr(null); } }, (e: unknown) => { if (alive) setErr(errText(e)); });
    return () => { alive = false; };
  }, [tick]);
  useEffect(() => () => window.clearTimeout(timer.current), []);
  const say = useCallback((t: string, isErr = false, undo?: Toast["undo"]) => {
    window.clearTimeout(timer.current);
    setToast({ t, err: isErr, undo });
    timer.current = window.setTimeout(() => setToast(null), undo ? 10_000 : 3_500);
  }, []);

  const done = (d: PayDone) => {
    setSheet(null); setOpen(d.payout.staff_id); reload();
    say(`Выдано: ${d.name} ${rub(d.payout.amount)} · холд ${signedRub(d.holdAfter)}`, false, { id: d.payout.id, amount: d.payout.amount });
  };
  const undo = async (u: NonNullable<Toast["undo"]>) => {
    if (undoing.current) return;
    undoing.current = true;
    try {
      const r = await cancelPayout(u.id, "отменено сразу после выдачи");
      say(r.already ? "Эта выдача уже была отменена" : `Выдача ${rub(u.amount)} отменена · холд ${signedRub(r.hold_after)}`);
      reload();
    } catch (e) { say(errText(e), true); } finally { undoing.current = false; }
  };

  const rows = data?.rows ?? [];
  const owed = rows.filter((r) => r.hold > 0), debt = rows.filter((r) => r.hold < 0);
  const sum = (xs: HoldRow[]) => xs.reduce((a, r) => a + r.hold, 0);
  return (
    <div className={board.content}>
      <div className={css.seg}>
        <button type="button" className={`${css.segBtn} ${mode === "balance" ? css.segOn : ""}`} onClick={() => setMode("balance")}>Баланс</button>
        <button type="button" className={`${css.segBtn} ${mode === "history" ? css.segOn : ""}`} onClick={() => setMode("history")}>История выдач</button>
      </div>
      {err && !data ? <div className={css.err}>{err}</div> : null}
      {err && data ? <div className={css.err}>Не удалось обновить холд — цифры могут быть устаревшими. <button type="button" className={css.act} onClick={reload}>Повторить</button></div> : null}
      {mode === "history" ? <AllPayouts tick={tick} onChanged={reload} say={say} /> : !data ? (err ? null : <div className={css.empty}>Считаю холд…</div>) : (
        <>
          <div className={css.tiles}>
            <div className={`${css.tile} ${css.tileOwe}`}><span className={css.tLbl}>Мы должны · {owed.length} чел.</span><b className={css.owe}>{rub(sum(owed))}</b></div>
            <div className={`${css.tile} ${css.tileDebt}`}><span className={css.tLbl}>Нам должны · {debt.length} чел.</span><b className={css.debt}>{rub(-sum(debt))}</b></div>
          </div>
          <div className={css.note}>
            Холд = старт на 09.10 + заработано за закрытые недели − выдано. Считаем с недели 09.10–15.10{data.closed ? ` (закрыто по ${data.closed.to.slice(8, 10)}.${data.closed.to.slice(5, 7)})` : ""}.
            Идущая неделя {weekLabel(data.current)} войдёт в холд в пятницу.
          </div>
          <div className={css.list}>
            {rows.map((r) => (
              <div key={r.staff_id} className={css.item}>
                <div className={css.row}>
                  <button type="button" className={css.who} aria-expanded={open === r.staff_id} onClick={() => setOpen((o) => (o === r.staff_id ? null : r.staff_id))}>
                    <span className={css.name}>{r.name}</span>
                    <span className={`${css.sub} ${css[holdTone(r.hold)] ?? ""}`}>{holdNote(r.hold)}{r.current_week ? ` · идёт неделя ${signedRub(r.current_week)}` : ""}</span>
                  </button>
                  <span className={`${css.amt} ${css[holdTone(r.hold)] ?? ""}`}>{signedRub(r.hold)}</span>
                  <button type="button" className={css.pay} onClick={() => setSheet(r)}>Выдать</button>
                </div>
                {open === r.staff_id ? <StaffHistory staffId={r.staff_id} tick={tick} onChanged={reload} say={say} /> : null}
              </div>
            ))}
          </div>
        </>
      )}
      <PayoutSheet open={!!sheet} row={sheet} onClose={() => setSheet(null)} onDone={done} onStale={reload}
        pendingId={sheet ? pending.current.get(sheet.staff_id) : undefined}
        onPending={(sid, id) => { if (id) pending.current.set(sid, id); else pending.current.delete(sid); }} />
      {toast ? (
        <div className={`${css.toast} ${toast.err ? css.toastErr : ""}`} role="status">
          <span>{toast.t}</span>
          {toast.undo ? <button type="button" className={css.toastUndo} onClick={() => { const u = toast.undo!; setToast(null); void undo(u); }}>Отменить</button> : null}
        </div>
      ) : null}
    </div>
  );
}
